import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import LinkTelegramModal from "../link-telegram-modal";
import { useAuth } from "../../context/authContext";
import { usePlatform } from "../../hooks/usePlatform";
import { startLinkTelegram } from "../../api/auth";
import { getAuthApiError } from "../../utils/auth-api-error";

const PROMPT_STORAGE_KEY = "openway:link-telegram-prompt";
const SESSION_DISMISS_KEY = "openway:link-telegram-prompt-session";
const RETRY_AFTER_MS = 5 * 24 * 60 * 60 * 1000;
const MAX_DISMISSALS = 2;

type PromptStorage = {
  dismissCount: number;
  lastDismissedAt: number;
};

function isTelegramLinked(user: { linkedProviders: { provider: string }[] } | null): boolean {
  return user?.linkedProviders.some((p) => p.provider === "telegram") ?? false;
}

function readPromptStorage(): PromptStorage | null {
  try {
    const raw = localStorage.getItem(PROMPT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PromptStorage;
    if (
      typeof parsed.dismissCount !== "number" ||
      typeof parsed.lastDismissedAt !== "number"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writePromptStorage(state: PromptStorage): void {
  try {
    localStorage.setItem(PROMPT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function shouldShowLinkTelegramPrompt(): boolean {
  const state = readPromptStorage();
  if (!state) return true;
  if (state.dismissCount >= MAX_DISMISSALS) return false;
  if (state.dismissCount === 1) {
    return Date.now() - state.lastDismissedAt >= RETRY_AFTER_MS;
  }
  return true;
}

function recordPromptDismissed(): void {
  const prev = readPromptStorage();
  writePromptStorage({
    dismissCount: (prev?.dismissCount ?? 0) + 1,
    lastDismissedAt: Date.now(),
  });
}

function isDismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function markDismissedThisSession(): void {
  try {
    sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
  } catch {
    /* ignore */
  }
}

/**
 * На сайте (не в Telegram Mini App) предлагает привязать Telegram,
 * если провайдер ещё не привязан. До 2 показов: сразу и через 5 дней.
 */
function LinkTelegramPromptGate() {
  const { status, user } = useAuth();
  const { isWeb } = usePlatform();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const telegramLinked = useMemo(() => isTelegramLinked(user), [user]);

  const canPrompt = useMemo(() => {
    if (!isWeb) return false;
    if (status !== "authed" || !user) return false;
    if (telegramLinked) return false;
    if (!shouldShowLinkTelegramPrompt()) return false;
    if (isDismissedThisSession()) return false;
    if (location.pathname.startsWith("/login")) return false;
    return true;
  }, [isWeb, status, user, telegramLinked, location.pathname]);

  useEffect(() => {
    if (!canPrompt) {
      setOpen(false);
      setError(null);
      return;
    }
    setOpen(true);
  }, [canPrompt]);

  const handleDismiss = useCallback(() => {
    recordPromptDismissed();
    markDismissedThisSession();
    setError(null);
    setOpen(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    setError(null);
    setSubmitting(true);
    const popup = window.open("about:blank", "_blank");
    try {
      const { deepLink } = await startLinkTelegram();
      markDismissedThisSession();
      setOpen(false);
      if (popup) {
        popup.location.replace(deepLink);
      } else {
        window.location.href = deepLink;
      }
    } catch (e) {
      if (popup && !popup.closed) {
        popup.close();
      }
      setError(getAuthApiError(e, "Не удалось открыть Telegram. Попробуйте позже."));
    } finally {
      setSubmitting(false);
    }
  }, []);

  return (
    <LinkTelegramModal
      open={open}
      onClose={handleDismiss}
      onConfirm={() => void handleConfirm()}
      loading={submitting}
      error={error}
    />
  );
}

export default LinkTelegramPromptGate;
